import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { cva, VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const avatarVariants = cva(
  "items-center justify-center font-medium uppercase transition ease-in-out hover:cursor-pointer",
  {
    variants: {
      size: {
        xs: "h-6 w-6 text-xs leading-normal",
        md: "h-10 w-10 text-sm",
      },
      color: {
        plum: "bg-plum-surface text-on-plum-surface",
        pink: "bg-pink-surface text-on-pink-surface",
        lilac: "bg-lilac-surface text-on-lilac-surface",
        ochre: "bg-ochre-surface text-on-ochre-surface",
      },
    },
    defaultVariants: {
      size: "md",
      color: "lilac",
    },
  },
)

type AvatarProps = React.ComponentPropsWithRef<typeof AvatarPrimitive.Root>
export const Avatar = ({ className, ref, ...props }: AvatarProps) => (
  <AvatarPrimitive.Root ref={ref} className={cn("flex shrink-0 overflow-hidden rounded-full", className)} {...props} />
)

type AvatarImageProps = React.ComponentPropsWithRef<typeof AvatarPrimitive.Image>
export const AvatarImage = ({ className, ref, ...props }: AvatarImageProps) => (
  <AvatarPrimitive.Image ref={ref} className={cn("aspect-square h-full w-full", className)} {...props} />
)

type AvatarFallbackProps = React.ComponentPropsWithRef<typeof AvatarPrimitive.Fallback>
export const AvatarFallback = ({ className, ref, ...props }: AvatarFallbackProps) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn("flex h-full w-full items-center justify-center rounded-full", className)}
    {...props}
  />
)

interface CustomAvatarProps
  extends Omit<React.ComponentProps<"img">, "color" | "ref">, VariantProps<typeof avatarVariants> {}

export const CustomAvatar = (props: CustomAvatarProps & Pick<AvatarProps, "ref">) => {
  const { className, size, color, children, ref, ...otherProps } = props
  return (
    <Avatar ref={ref} className={cn(avatarVariants({ size, color }), className)} {...otherProps}>
      {children}
    </Avatar>
  )
}
